import {
  CheckIcon,
  LayoutGridIcon,
  RotateCcwIcon,
  Table2Icon,
} from "lucide-react";

import { useDebouncedCallback } from "use-debounce";

import { JobPipelineKanbanSortBy } from "./kanban/JobPipelineKanbanSortBy";
import { JobPipelineFilterPills } from "@jobs/pipeline/filters/JobPipelineFilterPills";
import { JobPipelineFilterPopover } from "@jobs/pipeline/filters/JobPipelineFilterPopover";
import {
  Center,
  ClearableSearchInput,
  ErrorText,
  LoadingIndicator,
  Row,
} from "@/components";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useGetUserSettings } from "@/hooks/queries/useGetUserSettings";
import { useUpdateSetUserSettings } from "@/hooks/updateSets/useUpdateSetUserSettings";
import { defaultPipelineFilterAndSortState } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import {
  CandidacySource,
  Job,
  JobPipelineSortState,
  PipelineFilterAndSortState,
  PipelineViewPreference,
} from "@/types";

const MAX_COMMUTE_TRACK_DEBOUNCE_MS = 1000;

const FilterChip = ({
  label,
  active,
  count,
  onClick,
}: {
  label: string;
  active: boolean;
  count?: number;
  onClick: () => void;
}) => {
  return (
    <Button
      onClick={onClick}
      size="sm"
      variant="outline"
      className={cn(
        "shrink-0 gap-1.5 rounded-full text-xs",
        active
          ? "border-primary-400 bg-primary-50 text-primary-900"
          : "border-slate-200 text-slate-700 hover:bg-slate-50",
      )}
    >
      {active ? <CheckIcon className="h-3.5 w-3.5 text-primary-700" /> : null}
      {label}
      {count !== undefined ? (
        <span className="tabular-nums">({count})</span>
      ) : null}
    </Button>
  );
};

export const JobPipelineFiltersAndSort = ({
  allLocations,
  allSources,
  commsToSendCount,
  companyName,
  disabled,
  job,
  state,
  setState,
}: {
  allLocations: string[];
  allSources: CandidacySource[];
  commsToSendCount: number;
  companyName: string;
  disabled: boolean;
  job: Job;
  state: PipelineFilterAndSortState;
  setState: (next: PipelineFilterAndSortState) => void;
}) => {
  const {
    data: userSettings,
    isLoading: isUserSettingsLoading,
    isError: isUserSettingsError,
  } = useGetUserSettings();
  const { mutate: updateUserSettings } = useUpdateSetUserSettings();

  const pipelineView = userSettings?.pipelineViewPreference;
  const viewMeta = {
    tab_name: "Pipeline",
    ...(pipelineView ? { pipeline_view: pipelineView } : {}),
  };

  const setPipelineView = (next: PipelineViewPreference) => {
    if (!userSettings || next === userSettings.pipelineViewPreference) return;
    const previousValue = userSettings.pipelineViewPreference;
    const previousValueDurationHours =
      (Date.now() -
        new Date(userSettings.pipelineViewPreferenceUpdatedAt).getTime()) /
      3_600_000;

    // Want to track how long a user used kanban vs table for.
    mixpanelUtils.trackButtonClick("Pipeline view", MIXPANEL_SCREENS.JOB, {
      value: next,
      previous_value: previousValue,
      previous_value_duration_hours: previousValueDurationHours,
    });
    updateUserSettings({ pipelineViewPreference: next });
  };

  const setName = (name: string) => {
    setState({ ...state, name });
  };

  const setExcellentScore = (excellentScore: boolean) => {
    mixpanelUtils.trackFilterAndSortOptionSelected(
      MIXPANEL_SCREENS.JOB,
      "Excellent Score",
      excellentScore,
      viewMeta,
    );
    setState({ ...state, excellentScore });
  };

  const setGreatScore = (greatScore: boolean) => {
    mixpanelUtils.trackFilterAndSortOptionSelected(
      MIXPANEL_SCREENS.JOB,
      "Great Score",
      greatScore,
      viewMeta,
    );
    setState({ ...state, greatScore });
  };

  const setOnlyCommsToSend = (onlyCommsToSend: boolean) => {
    mixpanelUtils.trackFilterAndSortOptionSelected(
      MIXPANEL_SCREENS.JOB,
      "Comms to send",
      onlyCommsToSend,
      {
        ...viewMeta,
        job_id: job.id,
        company_id: job.companyId,
        job_title: job.title,
        company_name: companyName,
        comms_to_send_count: commsToSendCount,
        job_priority: job.priority,
      },
    );
    setState({ ...state, onlyCommsToSend });
  };

  const setSources = (sources: CandidacySource[]) => {
    mixpanelUtils.trackFilterAndSortOptionSelected(
      MIXPANEL_SCREENS.JOB,
      "Source",
      sources,
      viewMeta,
    );
    setState({ ...state, sources });
  };

  const setLocations = (locationsValue: string[]) => {
    mixpanelUtils.trackFilterAndSortOptionSelected(
      MIXPANEL_SCREENS.JOB,
      "Location",
      locationsValue,
      viewMeta,
    );
    setState({ ...state, locations: locationsValue });
  };

  const setStevieCallCompleted = (stevieCallCompleted: boolean) => {
    mixpanelUtils.trackFilterAndSortOptionSelected(
      MIXPANEL_SCREENS.JOB,
      "Stevie Call Completed",
      stevieCallCompleted,
      viewMeta,
    );
    setState({ ...state, stevieCallCompleted });
  };

  // Debounce to not fire off 10's-100's of events per drag
  const trackMaxCommute = useDebouncedCallback(
    (maxCommuteSeconds: number | null) => {
      mixpanelUtils.trackFilterAndSortOptionSelected(
        MIXPANEL_SCREENS.JOB,
        "Max Commute",
        String(maxCommuteSeconds),
        viewMeta,
      );
    },
    MAX_COMMUTE_TRACK_DEBOUNCE_MS,
  );

  const setMaxCommuteSeconds = (maxCommuteSeconds: number | null) => {
    trackMaxCommute(maxCommuteSeconds);
    setState({ ...state, maxCommuteSeconds });
  };

  const setIncludeRejected = (includeRejected: boolean) => {
    mixpanelUtils.trackFilterAndSortOptionSelected(
      MIXPANEL_SCREENS.JOB,
      "Include Rejected",
      includeRejected,
      viewMeta,
    );
    setState({ ...state, includeRejected });
  };

  const setIncludeCouldNotContact = (includeCouldNotContact: boolean) => {
    mixpanelUtils.trackFilterAndSortOptionSelected(
      MIXPANEL_SCREENS.JOB,
      "Include Could Not Contact",
      includeCouldNotContact,
      viewMeta,
    );
    setState({ ...state, includeCouldNotContact });
  };

  const setIncludeEmploymentEnded = (includeEmploymentEnded: boolean) => {
    mixpanelUtils.trackFilterAndSortOptionSelected(
      MIXPANEL_SCREENS.JOB,
      "Include Employment Ended",
      includeEmploymentEnded,
      viewMeta,
    );
    setState({ ...state, includeEmploymentEnded });
  };

  const setSortKeyAndOrder = (sortKeyAndOrder: JobPipelineSortState) => {
    mixpanelUtils.trackFilterAndSortOptionSelected(
      MIXPANEL_SCREENS.JOB,
      "Sort by",
      `${sortKeyAndOrder.key} ${sortKeyAndOrder.direction}`,
      viewMeta,
    );
    setState({ ...state, sortKeyAndOrder });
  };

  const handleReset = () => {
    mixpanelUtils.trackButtonClick(
      "Reset pipeline filters",
      MIXPANEL_SCREENS.JOB,
      viewMeta,
    );
    setState({
      ...defaultPipelineFilterAndSortState,
      sortKeyAndOrder: state.sortKeyAndOrder,
    });
  };

  const isInDefaultState =
    state.name === defaultPipelineFilterAndSortState.name &&
    state.excellentScore === defaultPipelineFilterAndSortState.excellentScore &&
    state.greatScore === defaultPipelineFilterAndSortState.greatScore &&
    state.onlyCommsToSend ===
      defaultPipelineFilterAndSortState.onlyCommsToSend &&
    state.includeRejected ===
      defaultPipelineFilterAndSortState.includeRejected &&
    state.includeCouldNotContact ===
      defaultPipelineFilterAndSortState.includeCouldNotContact &&
    state.includeEmploymentEnded ===
      defaultPipelineFilterAndSortState.includeEmploymentEnded &&
    state.stevieCallCompleted ===
      defaultPipelineFilterAndSortState.stevieCallCompleted &&
    state.maxCommuteSeconds ===
      defaultPipelineFilterAndSortState.maxCommuteSeconds &&
    state.sources.length === 0 &&
    state.locations.length === 0;

  /* Render */
  const renderScrollableMiddle = () => (
    <div className="relative min-w-0 flex-1">
      <Row className="no-scrollbar items-center gap-2 overflow-x-auto pr-10">
        {pipelineView === PipelineViewPreference.KANBAN && (
          <JobPipelineKanbanSortBy
            disabled={disabled}
            setSortState={setSortKeyAndOrder}
            sortState={state.sortKeyAndOrder}
          />
        )}
        <Row className="shrink-0 items-center gap-1.5">
          <Label className="shrink-0 text-xs text-slate-500">
            Quick Filters:
          </Label>
          <FilterChip
            label="Excellent score"
            active={state.excellentScore}
            onClick={() => {
              setExcellentScore(!state.excellentScore);
            }}
          />
          <FilterChip
            label="Great score"
            active={state.greatScore}
            onClick={() => {
              setGreatScore(!state.greatScore);
            }}
          />
          {/* Hidden when there's nothing to send — but kept while active so the filter can still be turned off. */}
          {commsToSendCount > 0 || state.onlyCommsToSend ? (
            <FilterChip
              label="Comms to send"
              active={state.onlyCommsToSend}
              count={commsToSendCount}
              onClick={() => {
                setOnlyCommsToSend(!state.onlyCommsToSend);
              }}
            />
          ) : null}
        </Row>
        <JobPipelineFilterPills
          setIncludeCouldNotContact={setIncludeCouldNotContact}
          setIncludeEmploymentEnded={setIncludeEmploymentEnded}
          setIncludeRejected={setIncludeRejected}
          setLocations={setLocations}
          setMaxCommuteSeconds={setMaxCommuteSeconds}
          setSources={setSources}
          setStevieCallCompleted={setStevieCallCompleted}
          state={state}
        />
      </Row>
      {/* Right-edge fade to show there's more content to scroll. */}
      <div className="pointer-events-none absolute right-0 top-0 h-full w-10 bg-gradient-to-l from-white to-transparent dark:from-[#212327]" />
    </div>
  );

  const renderViewToggle = () => (
    <Row className="shrink-0 items-center rounded-lg border border-slate-200 bg-slate-50 p-0.5">
      <Button
        className="h-8 w-8 rounded-md shadow-none"
        onClick={() => {
          setPipelineView(PipelineViewPreference.KANBAN);
        }}
        variant={
          pipelineView === PipelineViewPreference.KANBAN ? "secondary" : "ghost"
        }
      >
        <LayoutGridIcon className="h-4 w-4" />
      </Button>
      <Button
        className="h-8 w-8 rounded-md shadow-none"
        onClick={() => {
          setPipelineView(PipelineViewPreference.TABLE);
        }}
        variant={
          pipelineView === PipelineViewPreference.TABLE ? "secondary" : "ghost"
        }
      >
        <Table2Icon className="h-4 w-4" />
      </Button>
    </Row>
  );

  const render = () => {
    if (isUserSettingsLoading) {
      return <LoadingIndicator className="absolute inset-0" variant="large" />;
    }
    if (isUserSettingsError) {
      return (
        <Center>
          <ErrorText errorMessage="Error retrieving user settings" />
        </Center>
      );
    }

    return (
      <Row className="items-center gap-2">
        <ClearableSearchInput
          containerClassName="w-[260px] shrink-0"
          disabled={disabled}
          onChange={setName}
          placeholder="Search by name"
          trackingScreenName={MIXPANEL_SCREENS.JOB}
          trackingMetadata={viewMeta}
          value={state.name}
        />
        {renderScrollableMiddle()}
        <Row className="shrink-0 items-center gap-2">
          {!isInDefaultState && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label="Reset filters"
                    className="h-9 w-9 rounded-full p-0 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                    disabled={disabled}
                    onClick={handleReset}
                    size="icon"
                    variant="ghost"
                  >
                    <RotateCcwIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Reset filters</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <Row className="items-center gap-1.5">
            <Label
              className="cursor-pointer text-xs text-slate-600"
              htmlFor="pipeline-include-rejected"
            >
              Include Rejected
            </Label>
            <Switch
              checked={state.includeRejected}
              disabled={disabled}
              id="pipeline-include-rejected"
              onCheckedChange={setIncludeRejected}
            />
          </Row>
          <JobPipelineFilterPopover
            allLocations={allLocations}
            allSources={allSources}
            setIncludeCouldNotContact={setIncludeCouldNotContact}
            setIncludeEmploymentEnded={setIncludeEmploymentEnded}
            setLocations={setLocations}
            setMaxCommuteSeconds={setMaxCommuteSeconds}
            setSources={setSources}
            setStevieCallCompleted={setStevieCallCompleted}
            state={state}
          />
          {renderViewToggle()}
        </Row>
      </Row>
    );
  };

  return render();
};
